import { Flex, TextV2 } from '@withjoy/joykit';
import React from 'react';
import ReceiptBlock from '../../Blocks/ReceiptBlock';
import { StationeryPrintOrderDetailsFragment } from '@graphql/generated';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useSamplePackPrices } from '@apps/card/routes/Dashboard/components/DesignsGallery/components/SamplePack/Order/useSamplePackPrices';
import { useTranslation } from '@shared/core';

interface SamplePackPaymentDetailsProps {
  order: StationeryPrintOrderDetailsFragment;
}

export const SamplePackPaymentDetails: React.FC<SamplePackPaymentDetailsProps> = props => {
  const { order } = props;
  const { tq } = useTranslation('stationery');
  const formatPrice = useMinorUnitsToPriceString();

  const { taxInMinorUnits, priceInMinorUnits } = order;

  const prices = useSamplePackPrices({
    product: order.product!,
    discount: order.discount ? { promoCode: order.discount.code, amount: order.discount.discountAmountInMinorUnits || 0, type: 'flatDiscountAmount' } : undefined,
    deliveryMethod: order.deliveryMethod,
  });

  const productLineItem: [string, string] = [
    'A selection of cards showcasing each paper type, trim option, and card size',
    formatPrice(prices.basePrice.totalInMinorUnits, { canShowFreeLabel: true }),
  ];

  const shippingLineItem: [string, string] = [
    prices.shipping && 'deliveryMethodLabel' in prices.shipping ? prices.shipping.deliveryMethodLabel : '',
    formatPrice(prices.shipping?.totalPriceInMinorUnits || 0, { canShowFreeLabel: true }),
  ];

  const total = priceInMinorUnits + (taxInMinorUnits || 0);

  return (
    <Flex
      width={'100%'}
      maxWidth="100%"
      alignSelf="center"
      paddingX={{ _: 6, md: 0 }}
      flexDirection={{ _: 'column', md: 'row' }}
      rowGap={10}
      columnGap={140}
    >
      <Flex
        display={{ _: 'none', md: 'flex' }}
        flexDirection="column"
        flex={1}
        gap={10}
        maxWidth="400px"
      />
      <Flex
        flexDirection="column"
        flex={1}
      >
        <Flex
          rowGap={6}
          flexDirection="column"
          flex={1}
          maxWidth={{ md: '440px' }}
          width="100%"
          alignSelf="flex-end"
        >
          <TextV2 typographyVariant="hed3">{tq`Order Summary`}</TextV2>
          {productLineItem && (
            <ReceiptBlock
              title={tq`Sample Pack`}
              lines={[productLineItem]}
            />
          )}
          {shippingLineItem && (
            <ReceiptBlock
              title={tq`Shipping Method`}
              lines={[shippingLineItem]}
            />
          )}
          {!!taxInMinorUnits && (
            <ReceiptBlock
              title={tq`Taxes`}
              lines={[[tq`Sales Tax`, formatPrice(taxInMinorUnits, { canShowFreeLabel: true })]]}
            />
          )}
          <Flex
            flexDirection="column"
            paddingTop={3}
            borderTop="1px solid #EBEBEB"
          >
            <ReceiptBlock
              title={tq`Total`}
              lines={[[tq`Total`, formatPrice(total, { canShowFreeLabel: true })]]}
            />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
